import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import {
  BarChart3, Plus, ChevronDown, ChevronRight, Pencil, Check, X, Trash2,
  DollarSign, TrendingUp, RefreshCw, Upload, Save, MoreHorizontal
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { value: 'meta', label: 'Meta (Facebook/IG)' },
  { value: 'google', label: 'Google Ads' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'tv_radio', label: 'TV / Radio' },
  { value: 'other', label: 'Other' },
];

const MARKETS = [
  { value: 'jacksonville', label: 'Jacksonville' },
  { value: 'orlando', label: 'Orlando' },
  { value: 'tampa', label: 'Tampa' },
  { value: 'west-palm', label: 'West Palm' },
  { value: 'naples', label: 'Naples & Ft. Myers' },
  { value: 'gainesville', label: 'Gainesville' },
];

const formatCurrency = (v) => {
  if (!v && v !== 0) return '-';
  if (v >= 1000000) return `$${(v/1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v/1000).toFixed(1)}K`;
  return `$${Number(v).toFixed(0)}`;
};

const formatNumber = (v) => v != null ? Number(v).toLocaleString() : '-';

// Inline editable cell
const EditableCell = ({ value, onChange, type = 'number', prefix = '', className = '' }) => {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);

  const handleSave = () => {
    const parsed = type === 'number' ? parseFloat(tempValue) || 0 : tempValue;
    onChange(parsed);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        {prefix && <span className="text-xs text-muted-foreground">{prefix}</span>}
        <Input
          type={type}
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
          onBlur={handleSave}
          className="h-7 w-20 text-xs px-1"
          autoFocus
        />
      </div>
    );
  }

  return (
    <span
      className={`cursor-pointer hover:bg-primary/10 px-2 py-1 rounded transition-colors text-xs ${className}`}
      onClick={() => { setTempValue(value); setEditing(true); }}
      title="Click to edit"
    >
      {prefix}{type === 'number' && typeof value === 'number' ? (prefix === '$' ? formatCurrency(value) : formatNumber(value)) : (value || '-')}
    </span>
  );
};

// Ad row within a campaign
const AdRow = ({ ad, campaignId, onUpdate, onDelete }) => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');

  const handleFieldUpdate = async (field, value) => {
    try {
      await axios.put(
        `${API}/api/marketing/ad-spend/campaigns/${campaignId}/ads/${ad.id}`,
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const cpl = ad.spend > 0 && ad.leads > 0 ? (ad.spend / ad.leads) : 0;
  const roas = ad.spend > 0 && ad.revenue > 0 ? (ad.revenue / ad.spend) : 0;
  const com = ad.revenue > 0 && ad.spend > 0 ? (ad.spend / ad.revenue * 100) : 0;

  return (
    <tr className="border-b dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 text-xs" data-testid={`ad-row-${ad.id}`}>
      <td className="px-3 py-2 pl-10 text-muted-foreground">{ad.ad_name}</td>
      <td className="px-2 py-2 text-center">
        <Select value={ad.market || ''} onValueChange={(v) => handleFieldUpdate('market', v)}>
          <SelectTrigger className="h-6 text-xs w-24 border-0 bg-transparent"><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>{MARKETS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="px-2 py-2 text-right"><EditableCell value={ad.spend} onChange={(v) => handleFieldUpdate('spend', v)} prefix="$" /></td>
      <td className="px-2 py-2 text-right"><EditableCell value={ad.leads} onChange={(v) => handleFieldUpdate('leads', v)} /></td>
      <td className="px-2 py-2 text-right"><EditableCell value={ad.appointments} onChange={(v) => handleFieldUpdate('appointments', v)} /></td>
      <td className="px-2 py-2 text-right"><EditableCell value={ad.sales} onChange={(v) => handleFieldUpdate('sales', v)} /></td>
      <td className="px-2 py-2 text-right"><EditableCell value={ad.revenue} onChange={(v) => handleFieldUpdate('revenue', v)} prefix="$" /></td>
      <td className="px-2 py-2 text-right text-muted-foreground">{cpl > 0 ? formatCurrency(cpl) : '-'}</td>
      <td className="px-2 py-2 text-right text-muted-foreground">{roas > 0 ? `${roas.toFixed(2)}x` : '-'}</td>
      <td className="px-2 py-2 text-right text-muted-foreground">{com > 0 ? `${com.toFixed(1)}%` : '-'}</td>
      <td className="px-2 py-2 text-center">
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onDelete(ad.id)}>
          <Trash2 className="h-3 w-3 text-muted-foreground hover:text-red-500" />
        </Button>
      </td>
    </tr>
  );
};

// Campaign row (expandable)
const CampaignRow = ({ campaign, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [addingAd, setAddingAd] = useState(false);
  const [newAdName, setNewAdName] = useState('');
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');

  const t = campaign.totals || {};
  const adCount = campaign.ads?.length || 0;

  const handleAddAd = async () => {
    if (!newAdName.trim()) return;
    try {
      await axios.post(
        `${API}/api/marketing/ad-spend/campaigns/${campaign.id}/ads`,
        { ad_name: newAdName, market: campaign.market },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewAdName('');
      setAddingAd(false);
      onUpdate();
      toast.success('Ad added');
    } catch (e) {
      toast.error('Failed to add ad');
    }
  };

  const handleDeleteAd = async (adId) => {
    try {
      await axios.delete(
        `${API}/api/marketing/ad-spend/campaigns/${campaign.id}/ads/${adId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate();
    } catch (e) {
      toast.error('Failed to delete ad');
    }
  };

  const platformLabel = PLATFORMS.find(p => p.value === campaign.platform)?.label || campaign.platform;

  return (
    <>
      <tr
        className="border-b dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer text-sm font-medium"
        onClick={() => setExpanded(!expanded)}
        data-testid={`campaign-row-${campaign.id}`}
      >
        <td className="px-3 py-3">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            <span>{campaign.campaign_name}</span>
            <Badge variant="outline" className="text-[10px] px-1">{platformLabel}</Badge>
            <Badge variant="secondary" className="text-[10px] px-1">{adCount} ads</Badge>
          </div>
        </td>
        <td className="px-2 py-3 text-center text-xs text-muted-foreground">
          {MARKETS.find(m => m.value === campaign.market)?.label || '—'}
        </td>
        <td className="px-2 py-3 text-right font-semibold">{formatCurrency(t.spend)}</td>
        <td className="px-2 py-3 text-right">{formatNumber(t.leads)}</td>
        <td className="px-2 py-3 text-right">{formatNumber(t.appointments)}</td>
        <td className="px-2 py-3 text-right">{formatNumber(t.sales)}</td>
        <td className="px-2 py-3 text-right font-semibold">{formatCurrency(t.revenue)}</td>
        <td className={`px-2 py-3 text-right ${t.cpl > 485 ? 'text-red-500' : t.cpl > 405 ? 'text-yellow-500' : 'text-green-500'}`}>
          {t.cpl > 0 ? formatCurrency(t.cpl) : '-'}
        </td>
        <td className={`px-2 py-3 text-right ${t.roas < 5 ? 'text-red-500' : t.roas < 6.66 ? 'text-yellow-500' : 'text-green-500'}`}>
          {t.roas > 0 ? `${t.roas.toFixed(2)}x` : '-'}
        </td>
        <td className={`px-2 py-3 text-right ${t.com > 20 ? 'text-red-500' : t.com > 15 ? 'text-yellow-500' : 'text-green-500'}`}>
          {t.com > 0 ? `${t.com.toFixed(1)}%` : '-'}
        </td>
        <td className="px-2 py-3 text-center">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); onDelete(campaign.id); }}>
            <Trash2 className="h-3 w-3 text-muted-foreground hover:text-red-500" />
          </Button>
        </td>
      </tr>

      {expanded && campaign.ads?.map(ad => (
        <AdRow key={ad.id} ad={ad} campaignId={campaign.id} onUpdate={onUpdate} onDelete={handleDeleteAd} />
      ))}

      {expanded && (
        <tr className="border-b dark:border-slate-700/30">
          <td colSpan={11} className="px-3 py-2 pl-10">
            {addingAd ? (
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Ad name..."
                  value={newAdName}
                  onChange={(e) => setNewAdName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddAd(); }}
                  className="h-7 w-48 text-xs"
                  autoFocus
                />
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={handleAddAd}><Check className="h-3 w-3 mr-1" /> Add</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAddingAd(false)}><X className="h-3 w-3" /></Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={(e) => { e.stopPropagation(); setAddingAd(true); }}>
                <Plus className="h-3 w-3 mr-1" /> Add Ad
              </Button>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

// Main component
const AdSpendTracker = ({ period = 'mtd', onSpendUpdated }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [grandTotals, setGrandTotals] = useState({});
  const [marketSpend, setMarketSpend] = useState({});
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [addCampaignOpen, setAddCampaignOpen] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ campaign_name: '', platform: 'meta', market: '', budget: 0 });

  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/marketing/ad-spend/campaigns?period=${period}`, { headers });
      setCampaigns(res.data.campaigns || []);
      setGrandTotals(res.data.grand_totals || {});
      setMarketSpend(res.data.market_spend || {});
    } catch (e) {
      console.error('Failed to fetch campaigns:', e);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleAddCampaign = async () => {
    if (!newCampaign.campaign_name.trim()) return;
    try {
      await axios.post(`${API}/api/marketing/ad-spend/campaigns`, { ...newCampaign, period }, { headers });
      setAddCampaignOpen(false);
      setNewCampaign({ campaign_name: '', platform: 'meta', market: '', budget: 0 });
      fetchCampaigns();
      toast.success('Campaign created');
    } catch (e) {
      toast.error('Failed to create campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Delete this campaign and all its ads?')) return;
    try {
      await axios.delete(`${API}/api/marketing/ad-spend/campaigns/${campaignId}`, { headers });
      fetchCampaigns();
      toast.success('Campaign deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleImportCometly = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/api/marketing/ad-spend/import-from-cometly?period=${period}`, {}, { headers });
      toast.success(`Imported ${res.data.imported} campaigns from Cometly`);
      fetchCampaigns();
    } catch (e) {
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleSyncToPacing = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/api/marketing/ad-spend/sync-to-pacing?period=${period}`, {}, { headers });
      toast.success(`Synced $${res.data.total_spend?.toLocaleString()} to ${res.data.markets_updated} markets`);
      onSpendUpdated?.();
    } catch (e) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const gt = grandTotals;

  return (
    <Card className="dark:bg-slate-800/60" data-testid="ad-spend-tracker">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />
            Ad Spend Tracker
            <Badge variant="secondary" className="text-xs">{campaigns.length} campaigns</Badge>
            {campaigns.some(c => c.is_demo || c.source === 'demo') && (
              <Badge variant="outline" className="text-xs text-amber-500 border-amber-500">Demo Data</Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="text-xs gap-1" onClick={handleImportCometly} disabled={importing}>
              <Upload className={`h-3 w-3 ${importing ? 'animate-spin' : ''}`} />
              {importing ? 'Importing...' : 'Import Cometly'}
            </Button>
            <Button variant="outline" size="sm" className="text-xs gap-1" onClick={handleSyncToPacing} disabled={syncing}>
              <Save className={`h-3 w-3 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync to Pacing'}
            </Button>
            <Button size="sm" className="text-xs gap-1" onClick={() => setAddCampaignOpen(true)}>
              <Plus className="h-3 w-3" /> Add Campaign
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Campaign and ad-level spend tracking • Click any number to edit • Syncs to On-Track Pacing metrics
        </p>
      </CardHeader>

      <CardContent className="p-0">
        {/* Grand Totals Bar */}
        {gt.spend > 0 && (
          <div className="px-4 py-3 bg-primary/5 border-b dark:border-slate-700 grid grid-cols-5 gap-4 text-center">
            <div>
              <div className="text-xs text-muted-foreground">Total Spend</div>
              <div className="text-lg font-bold">{formatCurrency(gt.spend)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Total Leads</div>
              <div className="text-lg font-bold">{formatNumber(gt.leads)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">CPL</div>
              <div className={`text-lg font-bold ${gt.cpl > 485 ? 'text-red-500' : gt.cpl > 405 ? 'text-yellow-500' : 'text-green-500'}`}>
                {formatCurrency(gt.cpl)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">ROAS</div>
              <div className={`text-lg font-bold ${gt.roas < 5 ? 'text-red-500' : gt.roas < 6.66 ? 'text-yellow-500' : 'text-green-500'}`}>
                {gt.roas > 0 ? `${gt.roas.toFixed(2)}x` : '-'}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">COM %</div>
              <div className={`text-lg font-bold ${gt.com > 20 ? 'text-red-500' : gt.com > 15 ? 'text-yellow-500' : 'text-green-500'}`}>
                {gt.com > 0 ? `${gt.com.toFixed(1)}%` : '-'}
              </div>
            </div>
          </div>
        )}

        {/* Campaign Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-xs text-muted-foreground uppercase tracking-wide">
                <th className="px-3 py-2 text-left w-56">Campaign / Ad</th>
                <th className="px-2 py-2 text-center w-24">Market</th>
                <th className="px-2 py-2 text-right w-20">Spend</th>
                <th className="px-2 py-2 text-right w-16">Leads</th>
                <th className="px-2 py-2 text-right w-16">Appts</th>
                <th className="px-2 py-2 text-right w-16">Sales</th>
                <th className="px-2 py-2 text-right w-20">Revenue</th>
                <th className="px-2 py-2 text-right w-16">CPL</th>
                <th className="px-2 py-2 text-right w-16">ROAS</th>
                <th className="px-2 py-2 text-right w-16">COM%</th>
                <th className="px-2 py-2 text-center w-10"></th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(campaign => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  onUpdate={fetchCampaigns}
                  onDelete={handleDeleteCampaign}
                />
              ))}
              {campaigns.length === 0 && !loading && (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">
                    <div className="flex flex-col items-center gap-2">
                      <DollarSign className="h-8 w-8 text-muted-foreground/30" />
                      <p>No campaigns yet</p>
                      <p className="text-xs">Click "Add Campaign" or "Import Cometly" to get started</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>

      {/* Add Campaign Dialog */}
      <Dialog open={addCampaignOpen} onOpenChange={setAddCampaignOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Campaign</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Campaign Name</Label>
              <Input
                placeholder="e.g., Meta - Jacksonville Q1"
                value={newCampaign.campaign_name}
                onChange={(e) => setNewCampaign(p => ({ ...p, campaign_name: e.target.value }))}
                data-testid="new-campaign-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Platform</Label>
                <Select value={newCampaign.platform} onValueChange={(v) => setNewCampaign(p => ({ ...p, platform: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Market</Label>
                <Select value={newCampaign.market} onValueChange={(v) => setNewCampaign(p => ({ ...p, market: v }))}>
                  <SelectTrigger><SelectValue placeholder="All Markets" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Markets</SelectItem>
                    {MARKETS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Monthly Budget</Label>
              <Input
                type="number"
                placeholder="0"
                value={newCampaign.budget || ''}
                onChange={(e) => setNewCampaign(p => ({ ...p, budget: parseFloat(e.target.value) || 0 }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCampaignOpen(false)}>Cancel</Button>
            <Button onClick={handleAddCampaign} disabled={!newCampaign.campaign_name.trim()}>Create Campaign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdSpendTracker;
